(function () {
  'use strict';

  function initScrollScene(scene, stage) {
    if (
      !scene ||
      !stage ||
      window.DictacHome.prefersReducedMotion() ||
      !window.DictacHome.isDesktop()
    ) {
      return function () {};
    }

    var frame = 0;

    function reset() {
      scene.style.removeProperty('--favourites-progress');
      stage.style.removeProperty('--favourites-shift-y');
    }

    function update() {
      frame = 0;
      if (
        window.DictacHome.prefersReducedMotion() ||
        !window.DictacHome.isDesktop()
      ) {
        reset();
        return;
      }

      var rect = scene.getBoundingClientRect();
      var scrollRange = Math.max(1, scene.offsetHeight - window.innerHeight);
      var progress = Math.max(0, Math.min(1, -rect.top / scrollRange));
      var stageTravel = Math.max(0, stage.offsetHeight - window.innerHeight);
      var eased = 1 - Math.pow(1 - progress, 1.35);

      scene.style.setProperty('--favourites-progress', progress.toFixed(4));
      stage.style.setProperty(
        '--favourites-shift-y',
        (-stageTravel * eased).toFixed(2) + 'px'
      );
    }

    function requestUpdate() {
      if (!frame) frame = window.requestAnimationFrame(update);
    }

    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    return function () {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      reset();
    };
  }

  function findScene(root) {
    if (!root) return null;
    if (root.classList && root.classList.contains('dictac-carousel-scene'))
      return root;
    return root.closest('.dictac-carousel-scene');
  }

  function initCarousel(root) {
    if (!root || root.dataset.dictacCarouselInit) return;
    root.dataset.dictacCarouselInit = 'true';

    var scene = findScene(root);
    var stage = root.querySelector('[data-dictac-carousel-stage]');
    var viewport = root.querySelector('[data-dictac-carousel-viewport]');
    var track = root.querySelector('[data-dictac-carousel-track]');
    var cards = track
      ? Array.from(track.querySelectorAll('[data-dictac-carousel-card]'))
      : [];
    var prevBtn = root.querySelector('[data-dictac-carousel-prev]');
    var nextBtn = root.querySelector('[data-dictac-carousel-next]');
    var statusLabel = root.querySelector('[data-dictac-carousel-label]');
    var statusCount = root.querySelector('[data-dictac-carousel-count]');
    var statusBar = root.querySelector('[data-dictac-carousel-bar] span');
    var enablePin = root.dataset.scrollPin !== 'false';

    if (!viewport || !track || !cards.length) return;

    function isMobileLayout() {
      return window.matchMedia('(max-width: 620px)').matches;
    }

    var active = 0;
    var target = 0;
    var visual = 0;
    var trackX = 0;
    var animating = false;
    var locked = false;
    var timer = null;
    var pending = null;
    var drag = {
      pointerId: -1,
      startX: 0,
      startTrackX: 0,
      moved: false,
      delta: 0,
      velocity: 0,
      lastX: 0,
      lastTime: 0,
    };

    if (enablePin) initScrollScene(scene, stage);

    function positionForIndex(index) {
      var card = cards[index];
      if (!card) return 0;
      return (
        viewport.clientWidth / 2 - (card.offsetLeft + card.offsetWidth / 2)
      );
    }

    function stepSize() {
      if (cards.length < 2) return cards[0] ? cards[0].offsetWidth : 1;
      return Math.max(1, cards[1].offsetLeft - cards[0].offsetLeft);
    }

    function applyStyles() {
      if (isMobileLayout()) {
        track.style.removeProperty('--track-x');
        cards.forEach(function (card) {
          card.style.setProperty('--carousel-card-scale', '1');
          card.style.setProperty('--carousel-card-opacity', '1');
          card.classList.add('is-active');
          card.tabIndex = 0;
        });
        return;
      }

      track.style.setProperty('--track-x', trackX + 'px');
      cards.forEach(function (card, index) {
        var distance = Math.min(1, Math.abs(index - visual));
        card.style.setProperty(
          '--carousel-card-scale',
          String(1 - distance * 0.035)
        );
        card.style.setProperty(
          '--carousel-card-opacity',
          String(1 - distance * 0.22)
        );
        card.classList.toggle('is-active', index === active);
        card.tabIndex = index === active ? 0 : -1;
      });
      if (statusLabel && cards[active])
        statusLabel.textContent = cards[active].dataset.label || '';
      if (statusCount)
        statusCount.textContent = active + 1 + ' / ' + cards.length;
      if (statusBar)
        statusBar.style.width = ((active + 1) / cards.length) * 100 + '%';
      if (prevBtn) prevBtn.disabled = animating || active === 0;
      if (nextBtn) nextBtn.disabled = animating || active === cards.length - 1;
    }

    function finishAnimation() {
      if (!pending) return;
      var p = pending;
      pending = null;
      if (timer) clearTimeout(timer);
      timer = null;
      if (p.commit) active = p.target;
      target = p.commit ? p.target : active;
      visual = target;
      trackX = positionForIndex(target);
      animating = false;
      locked = false;
      applyStyles();
    }

    function startAnimation(index, commit, lockAlready) {
      var clamped = Math.max(0, Math.min(cards.length - 1, index));
      if (locked && !lockAlready) return;
      locked = true;
      target = clamped;
      pending = { target: clamped, commit: commit };
      if (timer) clearTimeout(timer);
      animating = true;
      visual = clamped;
      trackX = positionForIndex(clamped);
      applyStyles();
      var duration = window.DictacHome.prefersReducedMotion() ? 0 : 660;
      timer = setTimeout(finishAnimation, duration === 0 ? 0 : duration + 120);
    }

    function move(dir) {
      if (isMobileLayout()) return;
      startAnimation(active + dir, true);
    }

    function syncPosition() {
      if (isMobileLayout()) {
        applyStyles();
        return;
      }
      trackX = positionForIndex(target);
      visual = target;
      applyStyles();
    }

    syncPosition();
    if (window.ResizeObserver) {
      new ResizeObserver(syncPosition).observe(viewport);
    }

    if (prevBtn)
      prevBtn.addEventListener('click', function () {
        move(-1);
      });
    if (nextBtn)
      nextBtn.addEventListener('click', function () {
        move(1);
      });

    viewport.addEventListener('keydown', function (e) {
      if (isMobileLayout()) return;
      if (e.key === 'ArrowRight') move(1);
      if (e.key === 'ArrowLeft') move(-1);
    });

    viewport.addEventListener('pointerdown', function (e) {
      if (isMobileLayout()) return;
      if (
        e.button !== 0 ||
        locked ||
        e.target.closest('.dictac-carousel__link')
      )
        return;
      drag = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startTrackX: trackX,
        moved: false,
        delta: 0,
        velocity: 0,
        lastX: e.clientX,
        lastTime: e.timeStamp,
      };
      viewport.setPointerCapture(e.pointerId);
      viewport.classList.add('is-dragging');
    });

    viewport.addEventListener('pointermove', function (e) {
      if (drag.pointerId !== e.pointerId) return;
      var raw = e.clientX - drag.startX;
      if (Math.abs(raw) > 6) drag.moved = true;
      var delta = raw;
      if (
        (active === 0 && delta > 0) ||
        (active === cards.length - 1 && delta < 0)
      )
        delta *= 0.35;
      drag.delta = delta;
      drag.velocity =
        (e.clientX - drag.lastX) / Math.max(1, e.timeStamp - drag.lastTime);
      drag.lastX = e.clientX;
      drag.lastTime = e.timeStamp;
      trackX = drag.startTrackX + delta;
      visual = Math.max(
        0,
        Math.min(cards.length - 1, active - delta / stepSize())
      );
      applyStyles();
    });

    function endDrag(e) {
      if (drag.pointerId !== e.pointerId) return;
      if (viewport.hasPointerCapture(e.pointerId))
        viewport.releasePointerCapture(e.pointerId);
      viewport.classList.remove('is-dragging');
      if (!drag.moved) {
        drag.pointerId = -1;
        return;
      }
      var threshold = stepSize() * 0.18;
      var direction = drag.delta < 0 ? 1 : -1;
      var requested = active + direction;
      var canChange = requested >= 0 && requested < cards.length;
      var shouldChange =
        canChange &&
        (Math.abs(drag.delta) >= threshold || Math.abs(drag.velocity) >= 0.55);
      drag.pointerId = -1;
      locked = true;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          startAnimation(shouldChange ? requested : active, shouldChange, true);
        });
      });
    }

    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);

    viewport.addEventListener('click', function (e) {
      if (drag.moved) {
        e.preventDefault();
        e.stopPropagation();
        drag.moved = false;
        return;
      }
      if (e.target.closest('.dictac-carousel__link')) return;
      var card = e.target.closest('[data-dictac-carousel-card]');
      if (!card) return;
      var index = Number(card.dataset.index);
      if (isMobileLayout() || index === active) {
        var url = card.dataset.url;
        if (url) window.location.assign(url);
      } else {
        startAnimation(index, true);
      }
    });

    track.addEventListener('transitionend', function (e) {
      if (e.target === track && e.propertyName === 'transform')
        finishAnimation();
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document
      .querySelectorAll('[data-dictac-product-carousel]')
      .forEach(initCarousel);
  });

  document.addEventListener('shopify:section:load', function (e) {
    var root =
      e.target.querySelector('[data-dictac-product-carousel]') ||
      (e.target.matches('[data-dictac-product-carousel]') ? e.target : null);
    if (root) initCarousel(root);
  });
})();
